import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  alpha,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Badge as BadgeIcon,
  Shield as ShieldIcon,
  LocalHospital as ClinicIcon,
  VerifiedUser as StatusIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import useAuth from "../hooks/useAuth";
import { useClinicTheme } from "../theme";
import { getRoleLabel } from "../utils/roleLabels";
import { COLORS, TYPO } from "../styles/uiConstants";
import {
  useUpdateProfileMutation,
  useChangePasswordMutation,
} from "../store/userAccountApi";
import { useAlert } from "../context/AlertProvider";
import { FormTextField, PhoneInput, formatPhoneDisplay } from "../components/atoms";

const cardSx = {
  bgcolor: COLORS.white,
  borderRadius: 3,
  border: `1px solid ${COLORS.slate200}`,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
};

const modalBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  width: "100%",
  maxWidth: 440,
  maxHeight: "90vh",
  overflowY: "auto",
};

/**
 * İkon kutulu bilgi satırı — BayinerIOT profil kartındaki satır dili:
 * sol tarafta yumuşak tonlu ikon kutusu, sağda üstte küçük büyük-harf etiket,
 * altında değer.
 */
function InfoRow({ icon, label, value, accent, last = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.75,
        borderBottom: last ? "none" : `1px solid ${COLORS.slate100}`,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(accent, 0.1),
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: TYPO.caption,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: COLORS.slate500,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800 }} noWrap>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

/** Başlıklı sağ sütun kartı — küçük ikon çipi + başlık. */
function PanelCard({ icon, title, accent, children }) {
  return (
    <Box sx={{ ...cardSx, p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(accent, 0.1),
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: COLORS.slate800 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

const Profile = () => {
  const { auth, setAuth } = useAuth();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const accent = palette.primary;
  const roleLabel = getRoleLabel(auth?.role || auth?.user?.role);
  const displayRole = roleLabel && roleLabel !== "-" ? roleLabel : "Kullanıcı";
  const fullName = `${auth?.name || ""} ${auth?.surname || ""}`.trim() || "Kullanıcı";
  const accountStatus = auth?.accountStatus || "Aktif";
  const clinicName = auth?.clinic?.name || auth?.user?.clinic?.name || "—";

  // Mutations
  const [updateProfile, { isLoading: profileLoading }] =
    useUpdateProfileMutation();
  const [changePassword, { isLoading: passwordLoading }] =
    useChangePasswordMutation();

  // ── Profile state ──
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    surname: "",
    tellNo: "",
  });

  // ── Password state ──
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // ── Profile handlers ──
  const handleOpenProfileModal = () => {
    setProfileForm({
      name: auth?.name || "",
      surname: auth?.surname || "",
      tellNo: auth?.tellNo || "",
    });
    setOpenProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.name?.trim() || !profileForm.surname?.trim()) {
      showError("Ad ve soyad zorunludur.");
      return;
    }
    try {
      const result = await updateProfile({
        token: auth?.accessToken,
        ...profileForm,
      }).unwrap();
      setAuth({ ...auth, ...result.data.user });
      showSuccess("Profil güncellendi.");
      setOpenProfileModal(false);
    } catch (err) {
      showError(err?.data?.message || "Profil güncellenemedi.");
    }
  };

  // ── Password handlers ──
  const handleOpenPasswordModal = () => {
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShowPw(false);
    setShowNewPw(false);
    setOpenPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError("Yeni şifreler eşleşmiyor.");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      showError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }
    try {
      await changePassword({
        token: auth?.accessToken,
        passwordCurrent: passwordForm.currentPassword,
        password: passwordForm.newPassword,
        passwordConfirm: passwordForm.confirmPassword,
      }).unwrap();
      showSuccess("Şifre başarıyla değiştirildi.");
      setOpenPasswordModal(false);
    } catch (err) {
      showError(err?.data?.message || "Şifre değiştirilemedi.");
    }
  };

  const primaryButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2.5,
    py: 1.25,
    borderRadius: 2,
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: TYPO.body,
    fontWeight: 500,
    bgcolor: accent,
    color: palette.contrastText,
    boxShadow: `0 1px 2px ${palette.shadow}`,
    transition: "background-color 200ms ease",
    "&:hover": { bgcolor: palette.primaryDark },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  };

  const secondaryButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2.5,
    py: 1.25,
    borderRadius: 2,
    border: `2px solid ${accent}`,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: TYPO.body,
    fontWeight: 500,
    bgcolor: COLORS.white,
    color: accent,
    transition: "background-color 200ms ease, color 200ms ease",
    "&:hover": { bgcolor: accent, color: palette.contrastText },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  };

  // ── Render ──
  return (
    <Box sx={{ minHeight: "100%", bgcolor: COLORS.slate50, p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: { xs: 2, sm: 3 },
          alignItems: "stretch",
        }}
      >
        {/* ── SOL: Profil kartı ───────────────────────────────────── */}
        <Box sx={{ ...cardSx, display: "flex", flexDirection: "column" }}>
          {/* Başlık şeridi — avatar + ad + e-posta */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              px: 3,
              py: 3,
              bgcolor: COLORS.slate50,
              borderBottom: `1px solid ${COLORS.slate200}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  fontWeight: 700,
                  bgcolor: accent,
                  color: palette.contrastText,
                  border: `2px solid ${COLORS.white}`,
                  boxShadow: `0 4px 12px ${palette.shadow}`,
                }}
              >
                {fullName.charAt(0).toUpperCase()}
              </Box>
              <Box
                title="Aktif"
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  bgcolor: COLORS.success,
                  border: `2px solid ${COLORS.white}`,
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "1.25rem", fontWeight: 600, color: COLORS.slate800, letterSpacing: "-0.01em" }}
                noWrap
              >
                {fullName}
              </Typography>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate600, mt: 0.25 }} noWrap>
                {auth?.email || "—"}
              </Typography>
            </Box>
          </Box>

          {/* Bilgi satırları */}
          <Box sx={{ px: 3, py: 1, flex: 1, minHeight: 0 }}>
            <InfoRow
              icon={<PhoneIcon sx={{ fontSize: 20 }} />}
              label="Telefon"
              value={formatPhoneDisplay(auth?.tellNo)}
              accent={accent}
            />
            <InfoRow
              icon={<EmailIcon sx={{ fontSize: 20 }} />}
              label="E-posta"
              value={auth?.email}
              accent={accent}
            />
            <InfoRow
              icon={<ShieldIcon sx={{ fontSize: 20 }} />}
              label="Rol"
              value={displayRole}
              accent={accent}
            />
            <InfoRow
              icon={<LockIcon sx={{ fontSize: 20 }} />}
              label="Şifre"
              value="••••••••"
              accent={accent}
              last
            />
          </Box>

          {/* Aksiyonlar */}
          <Box sx={{ px: 3, pb: 3, pt: 1, mt: "auto", display: "flex", flexWrap: "wrap", gap: 1.5, flexShrink: 0 }}>
            <Box component="button" type="button" onClick={handleOpenProfileModal} sx={primaryButtonSx}>
              <EditIcon sx={{ fontSize: 18 }} />
              Profili Düzenle
            </Box>
            <Box component="button" type="button" onClick={handleOpenPasswordModal} sx={secondaryButtonSx}>
              <LockIcon sx={{ fontSize: 18 }} />
              Şifre Değiştir
            </Box>
          </Box>
        </Box>

        {/* ── SAĞ: Hesap + Güvenlik ──────────────────────────────── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 3 } }}>
          <PanelCard icon={<BadgeIcon sx={{ fontSize: 20 }} />} title="Hesap Bilgileri" accent={accent}>
            <Box>
              <InfoRow
                icon={<StatusIcon sx={{ fontSize: 20 }} />}
                label="Hesap Durumu"
                value={accountStatus}
                accent={accent}
              />
              <InfoRow
                icon={<ClinicIcon sx={{ fontSize: 20 }} />}
                label="Klinik"
                value={clinicName}
                accent={accent}
              />
              <InfoRow
                icon={<BadgeIcon sx={{ fontSize: 20 }} />}
                label="TC Kimlik No"
                value={auth?.tcNo}
                accent={accent}
                last
              />
            </Box>
          </PanelCard>

          <PanelCard icon={<ShieldIcon sx={{ fontSize: 20 }} />} title="Güvenlik" accent={accent}>
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate600, mb: 2 }}>
              Hesabınıza e-posta ve şifre ile giriş yapılır. Şifrenizi düzenli
              aralıklarla değiştirmeniz önerilir.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Chip
                label="Oturum: 60 gün"
                size="small"
                sx={{ fontWeight: 600, bgcolor: alpha(accent, 0.08), color: accent }}
              />
              <Box component="button" type="button" onClick={handleOpenPasswordModal} sx={secondaryButtonSx}>
                <LockIcon sx={{ fontSize: 18 }} />
                Şifre Değiştir
              </Box>
            </Box>
          </PanelCard>
        </Box>
      </Box>

      {/* ─── Profile Edit Modal ─── */}
      <Modal open={openProfileModal} onClose={() => setOpenProfileModal(false)}>
        <Box sx={modalBox}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: COLORS.slate800, mb: 2 }}>
            Profil Düzenle
          </Typography>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <FormTextField
              palette={palette}
              fullWidth
              label="Ad"
              value={profileForm.name}
              onChange={(e) =>
                setProfileForm((p) => ({ ...p, name: e.target.value }))
              }
              required
            />
            <FormTextField
              palette={palette}
              fullWidth
              label="Soyad"
              value={profileForm.surname}
              onChange={(e) =>
                setProfileForm((p) => ({ ...p, surname: e.target.value }))
              }
              required
            />
            <PhoneInput
              palette={palette}
              fullWidth
              label="Telefon"
              value={profileForm.tellNo}
              onChange={(v) => setProfileForm((p) => ({ ...p, tellNo: v }))}
            />
            <Box sx={{ display: "flex", gap: 1.5, pt: 1 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setOpenProfileModal(false)}
                sx={{
                  flex: 1,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.slate200}`,
                  bgcolor: COLORS.white,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: TYPO.body,
                  fontWeight: 500,
                  color: COLORS.slate600,
                  "&:hover": { bgcolor: COLORS.slate50 },
                }}
              >
                İptal
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={profileLoading}
                sx={{ ...primaryButtonSx, flex: 1, justifyContent: "center" }}
              >
                {profileLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <>
                    <SaveIcon sx={{ fontSize: 18 }} />
                    Kaydet
                  </>
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* ─── Password Change Modal ─── */}
      <Modal
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
      >
        <Box sx={modalBox}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: COLORS.slate800, mb: 2 }}>
            Şifre Değiştir
          </Typography>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <FormTextField
              palette={palette}
              fullWidth
              label="Mevcut Şifre"
              type={showPw ? "text" : "password"}
              value={passwordForm.currentPassword}
              onChange={(e) =>
                setPasswordForm((p) => ({
                  ...p,
                  currentPassword: e.target.value,
                }))
              }
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPw(!showPw)}
                    >
                      {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              palette={palette}
              fullWidth
              label="Yeni Şifre"
              type={showNewPw ? "text" : "password"}
              value={passwordForm.newPassword}
              onChange={(e) =>
                setPasswordForm((p) => ({
                  ...p,
                  newPassword: e.target.value,
                }))
              }
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNewPw(!showNewPw)}
                    >
                      {showNewPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              palette={palette}
              fullWidth
              label="Yeni Şifre Tekrar"
              type={showNewPw ? "text" : "password"}
              value={passwordForm.confirmPassword}
              onChange={(e) =>
                setPasswordForm((p) => ({
                  ...p,
                  confirmPassword: e.target.value,
                }))
              }
              required
            />
            <Box sx={{ bgcolor: alpha(accent, 0.06), borderRadius: 2, p: 1.5 }}>
              <Typography sx={{ fontSize: TYPO.caption, color: accent }}>
                Şifreniz en az 6 karakter olmalıdır.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, pt: 1 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setOpenPasswordModal(false)}
                sx={{
                  flex: 1,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.slate200}`,
                  bgcolor: COLORS.white,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: TYPO.body,
                  fontWeight: 500,
                  color: COLORS.slate600,
                  "&:hover": { bgcolor: COLORS.slate50 },
                }}
              >
                İptal
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={
                  passwordLoading ||
                  !passwordForm.currentPassword ||
                  !passwordForm.newPassword ||
                  !passwordForm.confirmPassword
                }
                sx={{ ...primaryButtonSx, flex: 1, justifyContent: "center" }}
              >
                {passwordLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Şifreyi Değiştir"
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default Profile;
